// src/components/ContactAndMapPage.jsx

import React, { useRef, forwardRef, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import Papa from 'papaparse'; // Import PapaParse
import './ContactAndMapPage.css';

// ===================================
//      PARENT COMPONENT (MODIFIED FOR DATA FETCHING)
// ===================================
const ContactAndMapPage = () => {
  const mapSectionRef = useRef(null);

  // --- NEW: State variables for dynamic contact info ---
  const [contactInfo, setContactInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- NEW: useEffect to fetch data from the Google Sheet ---
  useEffect(() => {
    // PASTE YOUR NEW GOOGLE SHEET URL HERE
    const CONTACT_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vTzIyZxxb_iHDFdrVrA6pvkY-Xqo27Fbv1U44HP_m7S4gwVLWfQJMf2juINo1p1CwTjExQN_3EbaqwJ/pub?output=csv";
    
    Papa.parse(CONTACT_SHEET_URL, {
        download: true,
        header: true,
        complete: (results) => {
            // Since we only have one row of data, we take the first item.
            const info = results.data[0]; 
            if (info) {
                setContactInfo(info);
            } else {
                setError("Contact information could not be loaded.");
            }
            setIsLoading(false);
        },
        error: (err) => {
            console.error("Error fetching contact data:", err);
            setError("Failed to load contact information.");
            setIsLoading(false);
        },
    });
  }, []);

  const handleScrollToMap = () => {
    if (!mapSectionRef.current) return;
    const navbarOffset = 80;
    const elementPosition = mapSectionRef.current.offsetTop;
    const offsetPosition = elementPosition - navbarOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };
  
  // Render a loading state while fetching
  if (isLoading) {
    return <div className="contact-page-wrapper"><div className="contact-container" style={{textAlign: 'center', padding: '5rem'}}>Loading Contact Info...</div></div>;
  }

  // Render an error state if fetching fails
  if (error) {
    return <div className="contact-page-wrapper"><div className="contact-container" style={{textAlign: 'center', color: 'red', padding: '5rem'}}>{error}</div></div>;
  }

  return (
    <>
      {/* Pass the fetched data down as props */}
      <ContactForm onScrollToMap={handleScrollToMap} contactInfo={contactInfo} />
      <GlobalPresence ref={mapSectionRef} contactInfo={contactInfo} />
    </>
  );
};
export default ContactAndMapPage;


// --- Other Components & Logic (UNCHANGED) ---
const LocationIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C7.589 2 4 5.589 4 10C4 12.899 6.279 16.324 11.272 21.619C11.662 22.039 12.338 22.039 12.728 21.619C17.721 16.324 20 12.899 20 10C20 5.589 16.411 2 12 2ZM12 12C10.895 12 10 11.105 10 10C10 8.895 10.895 8 12 8C13.105 8 14 8.895 14 10C14 11.105 13.105 12 12 12Z" /></svg>;
const PhoneIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19.264 15.064C18.283 14.083 17.519 14.168 17.064 14.623L15.82 15.867C13.606 14.712 11.288 12.394 10.133 10.18L11.377 8.936C11.832 8.481 11.917 7.717 10.936 6.736L7.96 3.76C6.979 2.779 6.216 2.864 5.76 3.32L4.053 5.027C3.12 5.96 3.333 7.208 4.646 9.699C6.273 12.73 8.783 15.936 12.217 18.232C14.777 19.986 16.035 20.106 16.978 19.163L18.684 17.457C19.139 17.001 20.244 16.044 19.264 15.064Z" /></svg>;
const EmailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4C2.897 4 2 4.897 2 6V18C2 19.103 2.897 20 4 20H20C21.103 20 22 19.103 22 18V6C22 4.897 21.103 4 20 4ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" /></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20Z" /><path d="M13 7H11V12H16V10H13V7Z" /></svg>;

const contactSchema = yup.object().shape({
  from_name: yup.string().required('Your name is required.'),
  from_email: yup.string().email('Please enter a valid email.').required('Your email is required.'),
  from_phone: yup.string().required('Phone number is required.'),
  message: yup.string().required('A message is required.').min(10, 'Message must be at least 10 characters.'),
  recaptcha: yup.string().required('Please verify you are not a robot.'),
});

// --- ContactForm Component MODIFIED to accept props ---
const ContactForm = ({ onScrollToMap, contactInfo }) => {
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbyaWFr8-FboH-V7du2xZ60KHmhtu4nbnAwpvYxmzpgT9wqy3vDtdHMl9djxw_eXxOBw7A/exec";
  const RECAPTCHA_SITE_KEY = "6LdG4tcrAAAAACrpa9DG-_S4Jv4SFph_gFP4N8rj";
  const [status, setStatus] = useState({ type: '', message: '' });
  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(contactSchema),
  });
  const onSubmit = async (data) => {
    setStatus({ type: 'info', message: 'Sending your message...' });
    const payload = { name: data.from_name, email: data.from_email, phone: data.from_phone, message: data.message, recaptchaToken: data.recaptcha };
    try {
      const response = await fetch(SCRIPT_URL, {
        method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.result === 'success') { setStatus({ type: 'success', message: result.message }); reset(); } 
      else { throw new Error(result.message || 'An unknown error occurred.'); }
    } catch (error) { setStatus({ type: 'error', message: `Submission failed: ${error.message}` }); }
  };
  return (
    <div className="contact-page-wrapper">
      <div className="contact-container">
        {/* Info panel now uses dynamic data from props */}
        <div className="info-panel">
            <div className="info-header"><h2>Get in <strong>touch</strong></h2><p>We are here when you <em>need</em> us the most.</p></div>
            <div className="info-section">
                <h4>The <strong>Office</strong></h4>
                <ul className="info-list">
                    <li className="scroll-to-map-trigger" onClick={onScrollToMap} title="Click to view on map">
                        <div className="icon-wrapper"><LocationIcon /></div>
                        <span><strong>Address:</strong> {contactInfo.address}</span>
                    </li>
                    <li>
                        <div className="icon-wrapper"><PhoneIcon /></div>
                        <span><strong>Phone:</strong> {contactInfo.phone1} {contactInfo.phone2 && `/ ${contactInfo.phone2}`}</span>
                    </li>
                    <li>
                        <div className="icon-wrapper"><EmailIcon /></div>
                        <span><strong>Email:</strong> {contactInfo.email}</span>
                    </li>
                </ul>
            </div>
            <div className="info-section">
                <h4>Business <strong>Hours</strong></h4>
                <ul className="info-list">
                    <li>
                        <div className="icon-wrapper"><ClockIcon /></div>
                        <span>{contactInfo.hours1}</span>
                    </li>
                    <li>
                        <div className="icon-wrapper"><ClockIcon /></div>
                        <span>{contactInfo.hours2}</span>
                    </li>
                </ul>
            </div>
        </div>
        {/* The form panel is unchanged */}
        <div className="form-panel"><form onSubmit={handleSubmit(onSubmit)} noValidate><div className="form-group"><input id="from_name" {...register('from_name')} placeholder="Full Name" /><p className="error-message">{errors.from_name?.message}</p></div><div className="form-group"><input id="from_email" {...register('from_email')} placeholder="Email Address" /><p className="error-message">{errors.from_email?.message}</p></div><div className="form-group"><input id="from_phone" {...register('from_phone')} placeholder="Phone Number" /><p className="error-message">{errors.from_phone?.message}</p></div><div className="form-group"><textarea id="message" {...register('message')} rows="5" placeholder="Your Message" /><p className="error-message">{errors.message?.message}</p></div><div className="form-group"><Controller name="recaptcha" control={control} render={({ field }) => (<ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={(token) => field.onChange(token)} />)} /><p className="error-message">{errors.recaptcha?.message}</p></div><button type="submit" className="submit-btn" disabled={isSubmitting}><span>{isSubmitting ? 'Sending...' : 'Submit Now'}</span></button>{status.message && (<p className={`status-message status-${status.type}`}>{status.message}</p>)}</form></div>
      </div>
    </div>
  );
};


// --- GlobalPresence Component MODIFIED to accept props ---
const GlobalPresence = forwardRef(({ contactInfo }, ref) => {
  // The map URL now comes dynamically from the props
  const mapEmbedUrl = contactInfo?.mapEmbedUrl;

  return (
    <div className="presence-section-wrapper" ref={ref}>
      <div className="main-viewport">
        {/* Render the iframe only if the mapEmbedUrl exists */}
        {mapEmbedUrl && (
          <iframe
            src={mapEmbedUrl}
            className="map-renderer-canvas"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location Map"
          ></iframe>
        )}
      </div>
    </div>
  );
});


/* ========================================================== */
/*     ALL PREVIOUS MAP IMPLEMENTATIONS (UNCHANGED & COMMENTED OUT)       */
/* ========================================================== */

/* 
// --- STATIC IMAGE + LINK IMPLEMENTATION (Use this if the iframe method fails) ---
const GlobalPresenceStatic = forwardRef((props, ref) => {
  // ... code ...
});
*/

/* 
// --- GOOGLE MAPS JS API IMPLEMENTATION (REQUIRES API KEY & BILLING) ---
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
// ... rest of the implementation ...
*/

/*
// --- MAPBOX GL JS IMPLEMENTATION (REQUIRES ACCESS TOKEN) ---
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// ... rest of the implementation ...
*/